// src/pages/subscription/components/BankTransferPayment.jsx
import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { 
  Building2, 
  Copy, 
  Upload, 
  File, 
  X, 
  Check, 
  AlertCircle,
  FileText,
  Image as ImageIcon
} from 'lucide-react';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { useAlert } from '../../../contexts/AlertContext';
import { formatPrice } from '../../../utils/subscriptionUtils';
import styles from './BankTransferPayment.module.css';

const BankTransferPayment = ({ 
  bankDetails, 
  amount, 
  currency = 'LKR',
  onSubmit, 
  isLoading = false 
}) => {
  const { t } = useLocalization();
  const { success, error } = useAlert();
  const [uploadedFile, setUploadedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [validationError, setValidationError] = useState('');

  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
  const ACCEPTED_TYPES = {
    'image/png': ['.png'],
    'image/jpeg': ['.jpg', '.jpeg'],
    'application/pdf': ['.pdf']
  };

  const validateFile = (file) => {
    // Check file type
    if (!Object.keys(ACCEPTED_TYPES).includes(file.type)) {
      return t('subscription.payment.invalidFileType');
    }

    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      return t('subscription.payment.fileTooLarge');
    }

    return null;
  };

  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    setValidationError('');

    if (rejectedFiles.length > 0) {
      const rejection = rejectedFiles[0];
      if (rejection.errors[0]?.code === 'file-too-large') {
        setValidationError(t('subscription.payment.fileTooLarge'));
      } else if (rejection.errors[0]?.code === 'file-invalid-type') {
        setValidationError(t('subscription.payment.invalidFileType'));
      }
      return;
    }

    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      const error = validateFile(file);

      if (error) {
        setValidationError(error);
        return;
      }

      setUploadedFile(file);

      // Create preview
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => setFilePreview(e.target.result);
        reader.readAsDataURL(file);
      } else {
        setFilePreview(null);
      }
    }
  }, [t]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ACCEPTED_TYPES,
    maxSize: MAX_FILE_SIZE,
    multiple: false,
    disabled: isLoading
  });

  const handleRemoveFile = () => {
    setUploadedFile(null);
    setFilePreview(null);
    setValidationError('');
  };

  const handleCopyAccountNumber = async () => {
    try {
      await navigator.clipboard.writeText(bankDetails.accountNumber);
      success(t('subscription.payment.accountNumberCopied'), {
        title: t('common.success')
      });
    } catch (err) {
      error(t('subscription.payment.copyFailed'), {
        title: t('common.error')
      });
    }
  };

  const handleSubmit = async () => {
    if (!uploadedFile) {
      setValidationError(t('subscription.payment.fileRequired'));
      return;
    }

    try {
      await onSubmit(uploadedFile);
    } catch (err) {
      console.error('Payment submission error:', err);
    }
  };

  const getFileIcon = () => {
    if (!uploadedFile) return null;
    
    if (uploadedFile.type.startsWith('image/')) {
      return <ImageIcon size={24} />;
    } else if (uploadedFile.type === 'application/pdf') {
      return <FileText size={24} />;
    }
    return <File size={24} />;
  };

  return (
    <div className={styles.bankTransferPayment}>
      {/* Payment Summary */}
      <div className={styles.paymentSummary}>
        <div className={styles.summaryRow}>
          <span className={styles.summaryLabel}>{t('subscription.payment.totalAmount')}</span>
          <span className={styles.summaryAmount}>{formatPrice(amount, currency)}</span>
        </div>
      </div>

      {/* Instructions */}
      <div className={styles.instructionsCard}>
        <AlertCircle size={20} className={styles.instructionIcon} />
        <p className={styles.instructionText}>
          {t('subscription.payment.bankTransferInstructions')}
        </p>
      </div>

      {/* Bank Details */}
      <div className={styles.bankDetailsCard}>
        <div className={styles.cardHeader}>
          <Building2 size={20} />
          <h3>{t('subscription.payment.bankDetails')}</h3>
        </div>

        <div className={styles.detailsGrid}>
          <div className={styles.detailItem}>
            <div className={styles.detailLabel}>{t('subscription.payment.accountHolder')}</div>
            <div className={styles.detailValue}>{bankDetails.accountHolder}</div>
          </div>

          <div className={styles.detailItem}>
            <div className={styles.detailLabel}>{t('subscription.payment.bankName')}</div>
            <div className={styles.detailValue}>{bankDetails.bankName}</div>
          </div>

          <div className={styles.detailItem}>
            <div className={styles.detailLabel}>{t('subscription.payment.branchName')}</div>
            <div className={styles.detailValue}>{bankDetails.branchName}</div>
          </div>

          <div className={styles.detailItem}>
            <div className={styles.detailLabel}>{t('subscription.payment.branchCode')}</div>
            <div className={styles.detailValue}>{bankDetails.branchCode}</div>
          </div>

          <div className={`${styles.detailItem} ${styles.accountNumberItem}`}>
            <div className={styles.detailLabel}>{t('subscription.payment.accountNumber')}</div>
            <div className={styles.accountNumberWrapper}>
              <div className={styles.detailValue}>{bankDetails.accountNumber}</div>
              <button
                type="button"
                className={styles.copyButton}
                onClick={handleCopyAccountNumber}
                title={t('subscription.payment.copyAccountNumber')}
              >
                <Copy size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* File Upload */}
      <div className={styles.uploadSection}>
        <h3 className={styles.uploadTitle}>{t('subscription.payment.uploadProof')}</h3>
        <p className={styles.uploadSubtitle}>{t('subscription.payment.uploadProofDescription')}</p>

        {!uploadedFile ? (
          <div
            {...getRootProps()}
            className={`${styles.dropzone} ${isDragActive ? styles.dropzoneActive : ''} ${isLoading ? styles.dropzoneDisabled : ''}`}
          >
            <input {...getInputProps()} />
            <Upload size={48} className={styles.uploadIcon} />
            <p className={styles.dropzoneText}>
              {isDragActive
                ? t('subscription.payment.dropFileHere')
                : t('subscription.payment.dragDropOrClick')}
            </p>
            <p className={styles.dropzoneHint}>
              {t('subscription.payment.supportedFormats')}
            </p>
          </div>
        ) : (
          <div className={styles.filePreview}>
            <div className={styles.fileInfo}>
              <div className={styles.fileIconWrapper}>
                {getFileIcon()}
              </div>
              <div className={styles.fileDetails}>
                <div className={styles.fileName}>{uploadedFile.name}</div>
                <div className={styles.fileSize}>
                  {(uploadedFile.size / 1024).toFixed(2)} KB
                </div>
              </div>
              <button
                type="button"
                className={styles.removeButton}
                onClick={handleRemoveFile}
                disabled={isLoading}
              >
                <X size={18} />
              </button>
            </div>

            {filePreview && (
              <div className={styles.imagePreview}>
                <img src={filePreview} alt="Payment proof preview" />
              </div>
            )}
          </div>
        )}

        {validationError && (
          <div className={styles.validationError}>
            <AlertCircle size={16} />
            <span>{validationError}</span>
          </div>
        )}
      </div>

      {/* Submit Button */}
      <div className={styles.submitSection}>
        <button
          type="button"
          className={styles.submitButton}
          onClick={handleSubmit}
          disabled={!uploadedFile || isLoading}
        >
          {isLoading ? (
            <>
              <div className={styles.spinner} />
              {t('subscription.payment.submittingProof')}
            </>
          ) : (
            <>
              <Check size={18} />
              {t('subscription.payment.submitProof')}
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default BankTransferPayment;